import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from '../../../ui/Menu';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON } from '../../mobile';
import { ClientDot } from './ClientDot';

/** Kontakt w grupie konfliktu (ten sam email u kilku klientów). */
export interface ConflictMember {
  contactId: string;
  name: string;
  clientId: string;
  clientName: string;
  clientColor: string | null;
}

/** Klienci grupy (jeden wpis na klienta; przy kilku kontaktach u klienta zostaje pierwszy). */
function byClient(members: ConflictMember[]): ConflictMember[] {
  const seen = new Set<string>();
  return members.filter((m) => {
    if (seen.has(m.clientId)) return false;
    seen.add(m.clientId);
    return true;
  });
}

function useResolve() {
  const utils = trpc.useUtils();
  const mutation = trpc.clients.contacts.resolveConflict.useMutation({
    onSuccess: () => utils.clients.invalidate(),
  });
  return mutation;
}

/**
 * Akcje rozstrzygnięcia konfliktu emaila (odbiór Etapu 1):
 *  - „To ta sama osoba — zostaw u X”: pozostałe kontakty z tym adresem trafiają do archiwum (z potwierdzeniem),
 *  - „To różne osoby — zostaw”: kontakty zostają, konflikt znika z listy.
 * `variant="buttons"` (lista w ustawieniach klienta) albo `variant="menu"` (baner przy kontakcie).
 */
export function ConflictActions({
  email,
  members,
  variant,
  disabled = false,
}: {
  email: string;
  members: ConflictMember[];
  variant: 'buttons' | 'menu';
  disabled?: boolean;
}) {
  const resolve = useResolve();
  const [keep, setKeep] = useState<ConflictMember | null>(null);
  const [error, setError] = useState<string | null>(null);
  const clients = byClient(members);

  async function differentPeople() {
    try {
      await resolve.mutateAsync({ decision: 'different_people', email });
      toast.success('Zostawiono kontakty u wszystkich klientów');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  async function samePerson() {
    if (!keep) return;
    setError(null);
    try {
      const out = await resolve.mutateAsync({ decision: 'same_person', email, keepContactId: keep.contactId });
      toast.success(`Kontakt zostaje u ${keep.clientName}`, {
        description:
          out.archivedContactIds.length > 0
            ? `Pozostałe kontakty z tym adresem są w archiwum (${out.archivedContactIds.length}).`
            : undefined,
      });
      setKeep(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  const others = keep ? members.filter((m) => m.contactId !== keep.contactId) : [];

  return (
    <>
      {variant === 'buttons' ? (
        <div className="flex flex-wrap items-center gap-2">
          {clients.map((m) => (
            <Button
              key={m.clientId}
              variant="secondary"
              size="s"
              disabled={disabled || resolve.isPending}
              onClick={() => setKeep(m)}
              className={TOUCH_BUTTON}
            >
              To ta sama osoba — zostaw u {m.clientName}
            </Button>
          ))}
          <Button
            variant="ghost"
            size="s"
            disabled={disabled}
            loading={resolve.isPending && !keep}
            onClick={differentPeople}
            className={TOUCH_BUTTON}
          >
            To różne osoby — zostaw
          </Button>
        </div>
      ) : (
        <Menu>
          <MenuTrigger asChild>
            <Button
              variant="ghost"
              size="s"
              iconRight={ChevronDown}
              disabled={disabled || resolve.isPending}
              className={TOUCH_BUTTON}
            >
              Rozstrzygnij
            </Button>
          </MenuTrigger>
          <MenuContent align="end" className="min-w-[280px]">
            <MenuLabel>To ta sama osoba — zostaw u:</MenuLabel>
            {clients.map((m) => (
              <MenuItem key={m.clientId} onSelect={() => setKeep(m)}>
                <span className="flex items-center gap-2">
                  <ClientDot color={m.clientColor} />
                  {m.clientName}
                </span>
              </MenuItem>
            ))}
            <MenuSeparator />
            <MenuItem onSelect={() => void differentPeople()}>To różne osoby — zostaw</MenuItem>
          </MenuContent>
        </Menu>
      )}

      <Dialog
        open={keep !== null}
        onOpenChange={(open) => {
          if (resolve.isPending) return;
          if (!open) {
            setKeep(null);
            setError(null);
          }
        }}
        width="s"
        title={`Zostawić kontakt u ${keep?.clientName ?? ''}?`}
        description={`To ta sama osoba (${email}). Pozostałe kontakty z tym adresem trafią do archiwum — możesz je później przywrócić.`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setKeep(null)} disabled={resolve.isPending} className={TOUCH_BUTTON}>
              Anuluj
            </Button>
            <Button variant="primary" onClick={samePerson} loading={resolve.isPending} className={TOUCH_BUTTON}>
              Zostaw u {keep?.clientName}
            </Button>
          </>
        }
      >
        {error && <FormMessage>{error}</FormMessage>}
        {others.length > 0 && (
          <ul className="mt-1 flex flex-col">
            {others.map((m) => (
              <li key={m.contactId} className="flex items-center gap-2 border-b border-line py-2 text-body-s">
                <ClientDot color={m.clientColor} />
                <span className="text-fg">{m.name}</span>
                <span className="text-fg-muted">· {m.clientName}</span>
                <span className="flex-1" />
                <span className="text-caption text-fg-muted">do archiwum</span>
              </li>
            ))}
          </ul>
        )}
      </Dialog>
    </>
  );
}
